// src/services/consultationService.js
import { collection, addDoc, query, where, getDocs, serverTimestamp, doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db, auth, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import adminService from './adminService';
import { getActivePerks } from './planPerksService';

class ConsultationService {
  /**
   * Book a consultation appointment
   * @param {Object} appointmentData - Appointment details
   * @returns {Promise<Object>} Result with appointment ID
   */
  async bookAppointment(appointmentData) {
    const userId = auth.currentUser?.uid;
    if (!userId) {
      return { success: false, error: 'User not authenticated' };
    }

    try {
      // Get user info
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      if (!userDoc.exists()) {
        return { success: false, error: 'User profile not found' };
      }
      const userData = userDoc.data();

      // Create appointment document (status 'confirmed' from start so we never need to update when Zoom fails)
      const appointmentRef = collection(db, 'consultations');
      
      // Ensure userId matches authenticated user (required by Firestore rules)
      const appointment = {
        userId: userId, // Must match request.auth.uid for Firestore rules
        userEmail: auth.currentUser.email,
        userName: userData?.displayName || auth.currentUser.displayName || 'Unknown',
        date: appointmentData.date, // ISO date string
        timeSlot: appointmentData.timeSlot, // e.g., "10:00"
        duration: 20, // 20 minutes
        status: 'confirmed',
        createdAt: serverTimestamp(),
        notes: appointmentData.notes || '',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };

      // Verify we're authenticated before creating
      if (!auth.currentUser) {
        return { success: false, error: 'User not authenticated' };
      }

      // Verify userId matches authenticated user (required by Firestore rules)
      if (appointment.userId !== auth.currentUser.uid) {
        console.error('User ID mismatch:', { 
          appointmentUserId: appointment.userId, 
          authUid: auth.currentUser.uid 
        });
        return { success: false, error: 'User ID mismatch' };
      }

      console.log('Creating consultation document:', {
        userId: appointment.userId,
        date: appointment.date,
        timeSlot: appointment.timeSlot
      });

      const docRef = await addDoc(appointmentRef, appointment);
      console.log('✅ Consultation document created:', docRef.id);

      // Create Zoom meeting (optional; booking succeeds either way)
      let zoomMeetingId = null;
      let zoomJoinUrl = null;
      let zoomPassword = null;

      try {
        const createZoomMeeting = httpsCallable(functions, 'createConsultationZoomMeeting');
        const zoomResult = await createZoomMeeting({
          appointmentDate: appointment.date,
          appointmentTime: appointment.timeSlot,
          userName: appointment.userName,
          timezone: appointment.timezone || 'UTC'
        });

        if (zoomResult.data) {
          zoomMeetingId = zoomResult.data.meetingId;
          zoomJoinUrl = zoomResult.data.joinUrl;
          zoomPassword = zoomResult.data.password;
        }
      } catch (zoomError) {
        console.error('Error creating Zoom meeting:', zoomError);
        // Continue: appointment already created. No updateDoc when Zoom fails → no permission issues.
      }

      // Only update when we have Zoom (add Zoom fields). When Zoom fails, skip update entirely.
      if (zoomMeetingId && zoomJoinUrl) {
        try {
          // Verify document exists and belongs to user before updating
          const docSnapshot = await getDoc(docRef);
          if (docSnapshot.exists() && docSnapshot.data().userId === userId) {
            await updateDoc(docRef, {
              zoomMeetingId,
              zoomJoinUrl,
              zoomPassword: zoomPassword || null
            });
          } else {
            console.warn('Cannot update consultation: document not found or access denied');
          }
        } catch (updateError) {
          console.error('Error updating consultation with Zoom info:', updateError);
          // Don't fail the booking if update fails - appointment is already created
        }
      }

      // Send confirmation email with Zoom link (only if Zoom meeting was created)
      if (zoomJoinUrl) {
        try {
          const sendConfirmation = httpsCallable(functions, 'sendConsultationConfirmation');
          await sendConfirmation({
            email: appointment.userEmail,
            displayName: appointment.userName,
            appointmentDate: appointment.date,
            appointmentTime: appointment.timeSlot,
            zoomJoinUrl: zoomJoinUrl,
            zoomPassword: zoomPassword
          });
          console.log('✅ Consultation confirmation email sent');
        } catch (emailError) {
          console.error('Error sending confirmation email:', emailError);
          // Don't fail the booking if email fails
        }
      } else {
        console.warn('⚠️ Zoom meeting not created, skipping confirmation email');
      }

      // Create admin notification (don't fail booking if this errors)
      try {
        await this.notifyAdmin({
          type: 'new_consultation',
          appointmentId: docRef.id,
          userId,
          userName: appointment.userName,
          userEmail: appointment.userEmail,
          date: appointment.date,
          timeSlot: appointment.timeSlot,
          zoomMeetingId: zoomMeetingId,
          zoomJoinUrl: zoomJoinUrl
        });
      } catch (notifyErr) {
        console.warn('Admin notification failed:', notifyErr);
      }

      return {
        success: true,
        appointmentId: docRef.id,
        zoomMeetingId: zoomMeetingId,
        zoomJoinUrl: zoomJoinUrl
      };
    } catch (error) {
      console.error('Error booking appointment:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get user's appointments
   * @param {string} userId - User ID
   * @returns {Promise<Array>} List of appointments
   */
  async getUserAppointments(userId) {
    try {
      const appointmentsRef = collection(db, 'consultations');
      const q = query(
        appointmentsRef,
        where('userId', '==', userId)
      );

      const querySnapshot = await getDocs(q);
      const appointments = [];

      querySnapshot.forEach((doc) => {
        appointments.push({
          id: doc.id,
          ...doc.data()
        });
      });

      // Sort by date (newest first)
      return appointments.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateB - dateA;
      });
    } catch (error) {
      console.error('Error fetching user appointments:', error);
      return [];
    }
  }

  /**
   * Check available time slots for a date
   * Only available on Monday, Tuesday, Thursday between 10:00-12:00 and 16:00-18:00
   * Booked slots are automatically excluded for all users
   * @param {string} date - ISO date string
   * @returns {Promise<Array>} Available time slots
   */
  async getAvailableTimeSlots(date) {
    try {
      // Check if the date is an available day (Monday, Tuesday, Thursday)
      if (!this.isAvailableDay(date)) {
        return []; // Return empty array for unavailable days
      }

      // Query all appointments for this date that are pending or confirmed
      // This ensures booked slots are unavailable to ALL users (not just the current user)
      // When a user books a slot, it's saved to Firestore and will be excluded here
      const appointmentsRef = collection(db, 'consultations');
      const q = query(
        appointmentsRef,
        where('date', '==', date),
        where('status', 'in', ['pending', 'confirmed'])
      );

      const querySnapshot = await getDocs(q);
      const bookedSlots = new Set();

      // Collect all booked time slots for this date from ALL users
      // This prevents double-booking - once a slot is taken, it's unavailable to everyone
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        bookedSlots.add(data.timeSlot);
      });

      // Generate all possible time slots (10:00-12:00 and 16:00-18:00, 20-minute intervals)
      const allSlots = this.generateTimeSlots();
      
      // Filter out booked slots - these are now unavailable to all users
      const availableSlots = allSlots.filter(slot => !bookedSlots.has(slot));
      
      return availableSlots;
    } catch (error) {
      console.error('Error fetching available slots:', error);
      // Return all slots if error, but only if it's an available day
      if (this.isAvailableDay(date)) {
        return this.generateTimeSlots();
      }
      return [];
    }
  }

  /**
   * Generate time slots (10:00 AM to 12:00 PM and 4:00 PM to 6:00 PM, 20-minute intervals)
   * Only available on Monday, Tuesday, Thursday
   * @returns {Array} Array of time slot strings
   */
  generateTimeSlots() {
    const slots = [];
    const intervalMinutes = 20;

    // Morning slots: 10:00 AM to 12:00 PM (10:00, 10:20, 10:40, 11:00, 11:20, 11:40)
    const morningStartHour = 10;
    const morningEndHour = 12;
    for (let hour = morningStartHour; hour < morningEndHour; hour++) {
      for (let minute = 0; minute < 60; minute += intervalMinutes) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push(timeString);
      }
    }

    // Afternoon slots: 4:00 PM to 6:00 PM (16:00, 16:20, 16:40, 17:00, 17:20, 17:40)
    const afternoonStartHour = 16;
    const afternoonEndHour = 18;
    for (let hour = afternoonStartHour; hour < afternoonEndHour; hour++) {
      for (let minute = 0; minute < 60; minute += intervalMinutes) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push(timeString);
      }
    }

    return slots;
  }

  /**
   * Check if a date is an available day (Monday, Tuesday, Thursday only)
   * @param {string} dateString - ISO date string (YYYY-MM-DD)
   * @returns {boolean} True if the date is Monday, Tuesday, or Thursday
   */
  isAvailableDay(dateString) {
    if (!dateString) return false;
    const date = new Date(dateString + 'T00:00:00');
    const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, 2 = Tuesday, 3 = Wednesday, 4 = Thursday, 5 = Friday, 6 = Saturday
    
    // Only Monday (1), Tuesday (2), and Thursday (4) are available
    return dayOfWeek === 1 || dayOfWeek === 2 || dayOfWeek === 4;
  }

  /**
   * Notify admin about new consultation booking.
   * Writes only to adminNotifications (users can create). adminLogs is admin-only.
   */
  async notifyAdmin(notificationData) {
    try {
      const notificationsRef = collection(db, 'adminNotifications');
      await addDoc(notificationsRef, {
        ...notificationData,
        read: false,
        createdAt: serverTimestamp(),
        priority: 'high'
      });
    } catch (error) {
      console.error('Error notifying admin:', error);
    }
  }

  /**
   * Whether the user may book consultations: Premium (5 sessions), customers
   * who bought monthly/quarterly before Oct 2026 (their old 1 / 3), and admins.
   * A 3-month plan bought since then includes no sessions.
   * @param {string} userId - User ID
   * @returns {Promise<boolean>}
   */
  async hasConsultationAccess(userId) {
    if (adminService.isAdmin()) return true;
    try {
      const perks = await getActivePerks(userId);
      return perks.consultations > 0;
    } catch (error) {
      console.error('Error checking consultation access:', error);
      return false;
    }
  }

  /**
   * Consultations left: the allowance of the running plan(s) minus what was
   * booked since that plan started.
   * @param {string} userId - User ID
   * @returns {Promise<number>} Number of remaining consultations
   */
  async getRemainingConsultations(userId) {
    // ADMIN OVERRIDE: effectively unlimited
    if (adminService.isAdmin()) return 999;
    try {
      const perks = await getActivePerks(userId);
      if (perks.consultations <= 0) return 0;

      const querySnapshot = await getDocs(query(
        collection(db, 'consultations'),
        where('userId', '==', userId)
      ));
      const bookedCount = querySnapshot.docs.filter((d) => {
        const created = d.data().createdAt?.toDate?.();
        // A booking still being written has no timestamp yet: count it.
        return !perks.since || !created || created >= perks.since;
      }).length;

      return Math.max(0, perks.consultations - bookedCount);
    } catch (error) {
      console.error('Error getting remaining consultations:', error);
      return 0;
    }
  }

  /**
   * Update an existing appointment
   * Only allowed if more than 24 hours remain before the appointment
   * @param {string} appointmentId - Appointment ID
   * @param {Object} updateData - Updated appointment details (date, timeSlot, notes)
   * @returns {Promise<Object>} Result with success status
   */
  async updateAppointment(appointmentId, updateData) {
    const userId = auth.currentUser?.uid;
    if (!userId) {
      return { success: false, error: 'User not authenticated' };
    }

    try {
      // Get the appointment to verify ownership and check time
      const appointmentRef = doc(db, 'consultations', appointmentId);
      const appointmentDoc = await getDoc(appointmentRef);

      if (!appointmentDoc.exists()) {
        return { success: false, error: 'Appointment not found' };
      }

      const appointment = appointmentDoc.data();

      // Verify ownership
      if (appointment.userId !== userId) {
        return { success: false, error: 'Unauthorized: This appointment does not belong to you' };
      }

      // Check if more than 24 hours remain
      const appointmentDateTime = new Date(`${appointment.date}T${appointment.timeSlot}:00`);
      const now = new Date();
      const hoursUntilAppointment = (appointmentDateTime - now) / (1000 * 60 * 60);

      if (hoursUntilAppointment <= 24) {
        return { success: false, error: 'Cannot edit appointment: Less than 24 hours remaining' };
      }

      // Validate new date if provided
      if (updateData.date) {
        const date = new Date(updateData.date + 'T00:00:00');
        const dayOfWeek = date.getDay();
        if (dayOfWeek !== 1 && dayOfWeek !== 2 && dayOfWeek !== 4) {
          return { success: false, error: 'Selected date is not available (only Monday, Tuesday, Thursday)' };
        }
      }

      // Call backend function to update appointment and send email
      const updateConsultation = httpsCallable(functions, 'updateConsultation');
      const result = await updateConsultation({
        appointmentId,
        oldDate: appointment.date,
        oldTimeSlot: appointment.timeSlot,
        newDate: updateData.date || appointment.date,
        newTimeSlot: updateData.timeSlot || appointment.timeSlot,
        notes: updateData.notes !== undefined ? updateData.notes : appointment.notes,
        userEmail: appointment.userEmail,
        userName: appointment.userName
      });

      if (result.data && result.data.success) {
        return { success: true, appointmentId };
      } else {
        return { success: false, error: result.data?.error || result.data?.message || 'Failed to update appointment' };
      }
    } catch (error) {
      console.error('Error updating appointment:', error);
      // Extract error message from Firebase error
      let errorMessage = 'Failed to update appointment';
      if (error.code === 'functions/internal') {
        errorMessage = 'Internal server error. Please try again or contact support.';
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.details) {
        errorMessage = error.details;
      }
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Delete an appointment
   * Only allowed if more than 24 hours remain before the appointment
   * @param {string} appointmentId - Appointment ID
   * @returns {Promise<Object>} Result with success status
   */
  async deleteAppointment(appointmentId) {
    const userId = auth.currentUser?.uid;
    if (!userId) {
      return { success: false, error: 'User not authenticated' };
    }

    try {
      // Get the appointment to verify ownership and check time
      const appointmentRef = doc(db, 'consultations', appointmentId);
      const appointmentDoc = await getDoc(appointmentRef);

      if (!appointmentDoc.exists()) {
        return { success: false, error: 'Appointment not found' };
      }

      const appointment = appointmentDoc.data();

      // Verify ownership
      if (appointment.userId !== userId) {
        return { success: false, error: 'Unauthorized: This appointment does not belong to you' };
      }

      // Check if more than 24 hours remain
      const appointmentDateTime = new Date(`${appointment.date}T${appointment.timeSlot}:00`);
      const now = new Date();
      const hoursUntilAppointment = (appointmentDateTime - now) / (1000 * 60 * 60);

      if (hoursUntilAppointment <= 24) {
        return { success: false, error: 'Cannot delete appointment: Less than 24 hours remaining' };
      }

      // Call backend function to delete appointment and send email
      const deleteConsultation = httpsCallable(functions, 'deleteConsultation');
      const result = await deleteConsultation({
        appointmentId,
        appointmentDate: appointment.date,
        appointmentTime: appointment.timeSlot,
        userEmail: appointment.userEmail,
        userName: appointment.userName
      });

      if (result.data.success) {
        return { success: true, appointmentId };
      } else {
        return { success: false, error: result.data.error || 'Failed to delete appointment' };
      }
    } catch (error) {
      console.error('Error deleting appointment:', error);
      return {
        success: false,
        error: error.message || 'Failed to delete appointment'
      };
    }
  }
}

export default new ConsultationService();
